import { useMemo, useState, useCallback, useEffect } from "react";
import { Button, Card, CardBody, CardHeader, Row, Col } from "reactstrap";
import clsx from "clsx";

import { apiSlice } from "app/apiSlice";
import DataGrid from "components/DataGrid";
import { showToast } from "components/Toasts/helpers/showToast";
import useAuthorizationCheck from "hooks/useAuthorizationCheck";

import { BulkPaymentModal } from "./BulkPaymentModal";
import { createGroupHeaderRenderer } from "./GroupHeaderRenderers";
import { formatGridCurrency } from "../utils/formatters";
import { useAccountingRowCount } from "../hooks/useAccountingRowCount";

import { createAccountingColDefs } from "../constants/accountingColDefs";
import "./accounting-grid.scss";
import {
  transformForecastData,
  isFullWidthRow,
  calculateForecastSummary,
  type GridRowData,
} from "../utils/transformForecastData";

// Move constant outside component to prevent recreation on every render
const EMPTY_ARRAY: GridRowData[] = [];

interface AccountingGridProps {
  className?: string;
  gridId?: string;
}

/**
 * Accounting Grid component that displays milestones grouped by date
 * using AG Grid Community features. Shows financial data for milestone payments.
 * Includes bulk payment functionality for paying all milestones at once.
 */
export const AccountingGrid = ({
  className,
  gridId = "accounting",
}: AccountingGridProps) => {
  // Permission checks
  const canMakePayment = useAuthorizationCheck(["make_payment"]);

  // State for bulk payment modal
  const [isBulkPaymentModalOpen, setIsBulkPaymentModalOpen] = useState(false);

  // Fetch payment dashboard data
  const {
    data: paymentData,
    isFetching,
    isLoading,
    refetch,
    error,
  } = apiSlice.useGetMilestonesPaymentDashboardQuery(undefined);

  useEffect(() => {
    if (error) {
      showToast({
        type: "error",
        message: "Error fetching accounting data. Please refresh the page.",
      });
    }
  }, [error]);

  // Transform payment data into grid rows with group headers
  const gridRowData = useMemo(() => {
    if (!paymentData || !Array.isArray(paymentData)) {
      return EMPTY_ARRAY;
    }

    return transformForecastData(paymentData);
  }, [paymentData]);

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    if (!paymentData || !Array.isArray(paymentData)) {
      return {
        totalCount: 0,
        approvedCount: 0,
        unapprovedCount: 0,
        totalAmount: 0,
        approvedAmount: 0,
        unapprovedAmount: 0,
        groupCount: 0,
      };
    }
    return calculateForecastSummary(paymentData);
  }, [paymentData]);

  // Get all milestones from forecast data for bulk payment
  const allMilestones = useMemo(() => {
    if (!paymentData || !Array.isArray(paymentData)) {
      return [];
    }
    // Extract all milestones from all date groups
    return paymentData.reduce((acc, dateGroup) => {
      if (dateGroup.items && Array.isArray(dateGroup.items)) {
        acc.push(...dateGroup.items);
      }
      return acc;
    }, []);
  }, [paymentData]);

  // Handlers for bulk payment modal
  const handlePayAllMilestones = useCallback(() => {
    setIsBulkPaymentModalOpen(true);
  }, []);

  const handleCloseBulkPaymentModal = useCallback(() => {
    setIsBulkPaymentModalOpen(false);
  }, []);

  const handleBulkPaymentSuccess = useCallback(() => {
    setIsBulkPaymentModalOpen(false);
    refetch(); // Refresh the grid data after payment
    showToast({
      type: "success",
      message: "Bulk payment completed successfully!",
    });
  }, [refetch]);

  const columnDefs = useMemo(() => {
    return createAccountingColDefs(canMakePayment);
  }, [canMakePayment]);

  const classes = clsx("accounting-grid", "h-100", className);

  // AG Grid configuration for full width rows
  const gridProps = useMemo(
    () => ({
      isFullWidthRow,
      fullWidthCellRenderer: createGroupHeaderRenderer(),
      suppressSorting: true,
      suppressMaintainUnsortedOrder: true,
      defaultColDef: { sortable: false },
    }),
    [], // No dependencies needed - all values are static
  );

  // Pay All Milestones button for grid header
  const PayAllButton = useCallback(
    () => (
      <Button
        color="primary"
        disabled={allMilestones.length === 0 || isLoading || isFetching}
        onClick={handlePayAllMilestones}
        className="d-flex align-items-center"
      >
        Pay All Milestones ({summaryStats.approvedCount})
      </Button>
    ),
    [
      allMilestones.length,
      summaryStats.approvedCount,
      isLoading,
      isFetching,
      handlePayAllMilestones,
    ],
  );

  return (
    <div className={clsx(classes, "d-flex flex-column h-100")}>
      {/* Summary Statistics */}
      <Card className="mb-3 py-2 flex-shrink-0">
        <CardBody className="py-1">
          <Row>
            <Col md={4}>
              <div className="text-center">
                <div className="h4">{summaryStats.approvedCount}</div>
                <small className="text-muted">Approved for Payment</small>
              </div>
            </Col>
            <Col md={4}>
              <div className="text-center">
                <div className="h4">
                  {formatGridCurrency(summaryStats.approvedAmount)}
                </div>
                <small className="text-muted">Amount Approved</small>
              </div>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Milestones Grid */}
      <div className="flex-grow-1 d-flex flex-column">
        <DataGrid
          appliedFilters={true}
          autoSizeEnabled={true}
          autoSizeIgnoreHeaders={false}
          autoSizeStrategy={{
            type: "fitCellContents",
          }}
          autoHeight={false}
          borders={true}
          columnDefs={columnDefs}
          gridId={gridId}
          idField="_id"
          isLoading={isLoading}
          isFetching={isFetching}
          refetch={refetch}
          quickFilter={false}
          rowData={gridRowData}
          // Pay All Milestones button in grid header
          HeaderActionElem={canMakePayment ? <PayAllButton /> : null}
          suppressRowClickSelection
          suppressCellFocus
          suppressRowHoverHighlight
          suppressDragLeaveHidesColumns
          suppressRowDrag
          customRowCountHook={useAccountingRowCount}
          {...gridProps}
        />
      </div>

      {/* Bulk Payment Modal for paying all milestones */}
      <BulkPaymentModal
        isOpen={isBulkPaymentModalOpen}
        onClose={handleCloseBulkPaymentModal}
        milestones={allMilestones}
        onSuccess={handleBulkPaymentSuccess}
        paymentType="all"
      />
    </div>
  );
};
